import React from 'react';
import { Link } from 'react-router-dom';
import styled from '@emotion/styled';

const HeaderContainer = styled.header`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(5px);
  padding: 1rem 2rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.98);
  }
`

const HeaderContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const Logo = styled(Link)`
  color: #4CAF50;
  text-decoration: none;
  font-size: 1.5rem;
  font-weight: bold;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: color 0.3s ease;

  &:hover {
    color: #45a049;
  }

  svg {
    width: 24px;
    height: 24px;
  }
`

const Nav = styled.nav`
  display: flex;
  gap: 1.5rem;
  
  a {
    color: #555;
    text-decoration: none;
    font-weight: 500;
    position: relative;
    padding: 0.5rem 0;
    transition: color 0.3s ease;

    &:after {
      content: '';
      position: absolute;
      width: 0;
      height: 2px;
      bottom: 0;
      left: 0;
      background-color: #4CAF50;
      transition: width 0.3s ease;
    }

    &:hover {
      color: #4CAF50;
      &:after {
        width: 100%;
      }
    }
  }
`

const FooterContainer = styled.footer`
  background: #f8f9fa;
  padding: 3rem 2rem;
  margin-top: 4rem;
  border-top: 1px solid #eee;
`

const FooterContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 2rem;
`

const FooterSection = styled.div`
  h3 {
    color: #4CAF50;
    margin-bottom: 1rem;
    font-size: 1.2rem;
  }

  p {
    color: #666;
    line-height: 1.6;
  }

  a {
    color: #555;
    text-decoration: none;
    display: block;
    margin: 0.5rem 0;
    transition: color 0.3s ease;

    &:hover {
      color: #4CAF50;
    }
  }
`

const Copyright = styled.div`
  text-align: center;
  padding-top: 2rem;
  margin-top: 2rem;
  border-top: 1px solid #eee;
  color: #666;
`

const MainContent = styled.main`
  min-height: calc(100vh - 200px); // Ensure footer stays at bottom
  padding-bottom: 4rem;
`

export function Header() {
  return (
    <HeaderContainer>
      <HeaderContent>
        <Logo to="/">
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
          </svg>
          Farmers Market Finder
        </Logo>
        <Nav>
          <Link to="/">Home</Link>
          <Link to="/about">About</Link>
        </Nav>
      </HeaderContent>
    </HeaderContainer>
  );
}

export function Footer() {
  return (
    <FooterContainer>
      <FooterContent>
        <FooterSection>
          <h3>About Us</h3>
          <p>
            Farmers Market Finder connects communities with local farmers markets,
            promoting sustainable agriculture and healthy food choices.
          </p>
        </FooterSection>
        <FooterSection>
          <h3>Quick Links</h3>
          <Link to="/about">About Us</Link>
          <Link to="/contact">Contact Us</Link>
          <Link to="/privacy">Privacy Policy</Link>
          <Link to="/terms">Terms of Service</Link>
        </FooterSection>
        <FooterSection>
          <h3>Contact</h3>
          <p>Email: contact@farmersmarketnear.me</p>
        </FooterSection>
      </FooterContent>
      <Copyright>
        <p> {new Date().getFullYear()} Farmers Market Finder | All Rights Reserved</p>
      </Copyright>
    </FooterContainer>
  );
}

export function Layout({ children }) {
  return (
    <>
      <Header />
      <MainContent>
        {children}
      </MainContent>
      <Footer />
    </>
  );
}
